import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

let refreshing = null;
axios.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config;
  if (error.response?.status !== 401 || !cfg || cfg._retry || cfg.url.includes("/auth/")) throw error;
  cfg._retry = true;
  try {
    refreshing = refreshing || axios.post(`${API}/auth/refresh`).finally(() => { refreshing = null; });
    await refreshing;
    return axios(cfg);
  } catch {
    window.dispatchEvent(new Event("auth:expired"));
    throw error;
  }
});

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (typeof e?.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return typeof detail.msg === "string" ? detail.msg : String(detail);
};

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  useEffect(() => {
    axios.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(() =>
      axios.post(`${API}/auth/refresh`).then((r) => setUser(r.data)).catch(() => setUser(false)));
    const onExpired = () => setUser(false);
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);
  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
  }, []);
  const logout = useCallback(async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    setUser(false);
  }, []);
  return <AuthCtx.Provider value={{ user, login, logout, isManager: user?.role === "manager" }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
